import React, { useState, useRef, useEffect } from "react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import IconEmoji from "@/icons/IconEmoji";

interface TextInputProps {
  value: string;
  setValue: (value: string) => void;
  showEmojiButton?: boolean;
  maxWordCount?: number;
  ariaLabel?: string;
}

export default function TextInput({
  value,
  setValue,
  showEmojiButton = true,
  maxWordCount,
  ariaLabel
}: TextInputProps) {
  const safeValue = typeof value === "string" ? value : "";
  const [internalValue, setInternalValue] = useState(safeValue);
  const [showPicker, setShowPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleEmojiSelect = (emoji: any) => {
    const emojiChar = emoji.native;
    const textarea = textareaRef.current;

    if (!textarea) return;

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const newValue =
      safeValue.slice(0, start) + emojiChar + safeValue.slice(end);
    setValue(newValue);

    setTimeout(() => {
      textarea.setSelectionRange(
        start + emojiChar.length,
        start + emojiChar.length
      );
      textarea.focus();
    }, 0);
  };

  const wordCount = safeValue.trim().split(/\s+/).filter(Boolean).length;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto"; // reset
      textarea.style.height = `${textarea.scrollHeight}px`; // grow to content
    }
  }, [safeValue]);

  return (
    <div className="w-full relative">
      <div className="border rounded-lg p-3 bg-white flex flex-col gap-2">
        <textarea
          ref={textareaRef}
          value={safeValue}
          aria-label={ariaLabel}
          onChange={(e) => {
            const newText = e.target.value;
            const newWordCount = newText
              .trim()
              .split(/\s+/)
              .filter(Boolean).length;

            if (!maxWordCount || newWordCount <= maxWordCount) {
              setValue(newText);
            }
          }}
          placeholder="Type your message"
          className="w-full outline-none bg-transparent resize-none"
          rows={1}
        />

        {showEmojiButton && (
          <div className="relative inline-block">
            <button
              ref={buttonRef}
              type="button"
              onClick={() => setShowPicker(!showPicker)}
              className="text-xl"
            >
              <IconEmoji className="text-slate-500 hover:text-cornflower-blue-500" />
            </button>

            {showPicker && (
              <div
                ref={pickerRef}
                className="absolute z-50 mt-2"
                style={{ left: 0 }}
              >
                <Picker data={data} onEmojiSelect={handleEmojiSelect} />
              </div>
            )}
          </div>
        )}
      </div>

      {maxWordCount && (
        <div className="mt-1 text-right text-sm text-gray-500">
          {wordCount} / {maxWordCount}
        </div>
      )}
    </div>
  );
}
